import {RequestResult} from './RequestResult';

export class RestClient {
  /**
   * Perform a GET request to receive a response of the given type T
   *
   * @param url of the REST resource
   * @param headers: optional additional headers
   * @constructor type of response T
   */
  public async GET(url: string, headers = {}): Promise<RequestResult> {
    try {
      const response = await fetch(url, {
        headers: headers,
        method: 'get',
      });
      return this.handleResponse(response);
    } catch (err) {
      return this.handleError(err);
    }
  }

  /**
   * Perform a POST request to create a new instance of the given payload and
   * receive a response of the given type T
   *
   * @param url of the REST resource
   * @param payload
   * @param headers: optional additional headers
   * @constructor type of response T
   */
  public async POST(url: string, payload: any, headers = {}): Promise<RequestResult> {
    try {
      const response = await fetch(url, {
        headers: headers,
        method: 'post',
        body: JSON.stringify(payload),
      });
      return this.handleResponse(response);
    } catch (err) {
      return this.handleError(err);
    }
  }

  /**
   * Perform a PUT request to update an existing instance of the given payload and
   * receive a response of the given type T
   *
   * @param url of the REST resource
   * @param payload
   * @param headers: optional additional headers
   * @constructor type of response T
   */
  public async PUT(url: string, payload: any, headers = {}): Promise<RequestResult> {
    try {
      const response = await fetch(url, {
        headers: headers,
        method: 'put',
        body: JSON.stringify(payload),
      });
      return this.handleResponse(response);
    } catch (err) {
      return this.handleError(err);
    }
  }

  /**
   * Perform a DELETE request to remove an instance
   *
   * @param url of the REST resource
   * @param headers: optional additional headers
   * @constructor type of response T
   */
  public async DELETE(url: string, headers = {}): Promise<RequestResult> {
    try {
      const response = await fetch(url, {
        headers: headers,
        method: 'delete',
      });
      return this.handleResponse(response);
    } catch (err) {
      return this.handleError(err);
    }
  }

  /**
   * @param url
   * @param headers: optional additional headers
   * @constructor
   */
  public async DOWNLOAD(url: string, headers = {}): Promise<Response> {
    return fetch(url, {
      headers: headers,
      method: 'GET',
    });
  }

  private handleError(error: Error) {
    return new RequestResult(null, error);
  }

  private async handleResponse(resp: Response) {
    if (((resp.status >= 200 && resp.status < 300) && !resp.redirected)) {
      const data = await resp.json();
      return new RequestResult(data, null);
    }
    if ((resp.status === 302 || resp.status === 200) && resp.redirected) {
      // clone the response to have access to the body stream even after json parse failed.
      try {
        const jsonResp = resp.clone();
        const data = await jsonResp.json();
        return new RequestResult(data, null);
      } catch (e) {
        const data = await resp.text();
        return new RequestResult(data, null);
      }
    }
    return new RequestResult(null, {
      message: 'Got bad response or redirect from REST API.',
      response: await resp.json(),
    });
  }
}
